/**
 * Minimal observable value, shaped for `useSyncExternalStore`.
 *
 * The snapshot is replaced, never mutated, so React's identity check works and
 * an engine can update at audio rate while the UI reads whatever the latest
 * value happens to be.
 */
export class Observable<T> {
  private value: T;
  private readonly listeners = new Set<() => void>();

  constructor(initial: T) {
    this.value = initial;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): T => this.value;

  set(next: T): void {
    if (Object.is(next, this.value)) return;
    this.value = next;
    for (const listener of this.listeners) listener();
  }

  /**
   * Merge a patch, skipping the notification when nothing actually changed.
   *
   * The engines call this on every analysis frame — a dozen times a second —
   * and most of those carry the same values as the last one. Without this check
   * every frame would allocate a new snapshot and re-render the whole exercise
   * screen, including the SVG chord diagram, for no visible difference.
   */
  update(patch: Partial<T>): void {
    const current = this.value as Record<string, unknown>;
    let changed = false;
    for (const key of Object.keys(patch) as (keyof T & string)[]) {
      if (!Object.is(current[key], (patch as Record<string, unknown>)[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.set({ ...this.value, ...patch });
  }
}
